import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n'
import { useStore } from '../data/store'
import {
  HOLIDAY_ATTRIBUTION, coverageFor, holidayName, toPeriods,
  type HolidayCoverage, type HolidayPeriod, type TenantHolidayRow,
} from '../lib/holidays'

/**
 * School and public holidays of the landlord's guest markets.
 *
 * WHY it exists: a Tyrolean landlord prices the week Bavaria and North Rhine-Westphalia
 * are both off school differently from the week either one is. That overlap is
 * invisible on a plain calendar.
 *
 * WHY it is drawn the way it is: an earlier version gave every region its own lane
 * above the grid. With seven guest markets that is thirty-odd lanes, all of them
 * solid yellow across July — it buried the calendar, pushed the unit rows out of
 * alignment with the frozen name column, and made the grid unusable. This is
 * background information for a pricing decision, not content. So it renders as a
 * thin bar under the date in the header and nowhere else — an earlier version tinted
 * the whole day column, which put a wash of colour across every booking in the grid
 * for the sake of a hint. The detail is on hover, and the tooltip leads with what the
 * bar is FOR, because a coloured stripe means nothing to someone who has not read the
 * legend at the bottom of the screen.
 *
 * Data is mirrored from the OpenHolidays API. The ODbL licence requires attribution
 * wherever it is shown — that is what `HOLIDAY_ATTRIBUTION` is for; it is a licence
 * condition, not decoration.
 */

/** What is going on, on one day, across everything the tenant watches. */
export interface HolidayDay {
  /** Distinct regions with school holidays. Region name in the current language. */
  school: string[]
  /** Distinct public holidays, as "Region: Name". */
  publicDays: string[]
  /** School regions off, as a fraction of the regions watched. 0 … 1. */
  density: number
}

function eachDayISO(from: string, toExcl: string, limit: number): string[] {
  const out: string[] = []
  const d = new Date(`${from}T00:00:00Z`)
  const end = new Date(`${toExcl}T00:00:00Z`)
  while (d < end && out.length < limit) {
    out.push(d.toISOString().slice(0, 10))
    d.setUTCDate(d.getUTCDate() + 1)
  }
  return out
}

/**
 * Loads the window's holidays and indexes them by day.
 *
 * The index is what the calendar wants: it draws per day column, so asking
 * "what is on the 4th of August" must not mean scanning every period.
 */
export function useTenantHolidays(from: string, to: string) {
  const store = useStore()
  const { lang } = useI18n()
  const [rows, setRows] = useState<TenantHolidayRow[]>([])
  const [coverage, setCoverage] = useState<HolidayCoverage[]>([])
  const tenantId = store.db.tenant.id

  useEffect(() => {
    if (!store.live || !supabase || !tenantId) { setRows([]); setCoverage([]); return }
    let alive = true
    void (async () => {
      const [h, c] = await Promise.all([
        supabase!.rpc('tenant_holidays', {
          p_tenant: tenantId, p_from: from, p_to: to, p_kind: null, p_regions: null,
        }),
        supabase!.rpc('tenant_holiday_coverage', { p_tenant: tenantId }),
      ])
      if (!alive) return
      setRows((h.data as TenantHolidayRow[] | null) ?? [])
      setCoverage((c.data as HolidayCoverage[] | null) ?? [])
    })()
    return () => { alive = false }
  }, [store.live, tenantId, from, to])

  const { byDay, regionCount } = useMemo(() => {
    const periods: HolidayPeriod[] = toPeriods(rows)
    const regions = new Set(periods.map((p) => `${p.country}:${p.region}`))
    const map = new Map<string, { school: Set<string>; publicDays: Set<string> }>()

    for (const p of periods) {
      const start = p.from > from ? p.from : from
      const stop = p.to < to ? p.to : to
      const region = holidayName(p.regionName, lang) || p.region
      for (const day of eachDayISO(start, stop, 400)) {
        let entry = map.get(day)
        if (!entry) { entry = { school: new Set(), publicDays: new Set() }; map.set(day, entry) }
        if (p.kind === 'school') entry.school.add(region)
        else entry.publicDays.add(`${region}: ${holidayName(p.name, lang)}`)
      }
    }

    const denom = Math.max(1, regions.size)
    const out = new Map<string, HolidayDay>()
    for (const [day, e] of map) {
      out.set(day, {
        school: [...e.school].sort(),
        publicDays: [...e.publicDays].sort(),
        density: Math.min(1, e.school.size / denom),
      })
    }
    return { byDay: out, regionCount: regions.size }
  }, [rows, from, to, lang])

  return { byDay, coverage, regionCount, hasData: rows.length > 0 }
}

/**
 * Hover text for a day column.
 *
 * Leads with what the bar IS, not only who is off: a coloured stripe under a date
 * means nothing to someone who has not read the legend at the bottom of the screen,
 * and nobody reads that twice.
 */
export function holidayTitle(day: HolidayDay | undefined, de: boolean): string | undefined {
  if (!day || (day.school.length === 0 && day.publicDays.length === 0)) return undefined
  const parts: string[] = [
    de
      ? 'Ferien und Feiertage Ihrer Gästemärkte — Hinweis für die Preisgestaltung.'
      : 'School and public holidays in your guest markets — a hint for pricing.',
  ]
  if (day.school.length) {
    parts.push(`${de ? 'Schulferien' : 'School holidays'}: ${day.school.join(', ')}`)
  }
  if (day.publicDays.length) {
    parts.push(`${de ? 'Feiertag' : 'Public holiday'}: ${day.publicDays.join(' · ')}`)
  }
  return parts.join('\n')
}

/**
 * The marker inside a date-header cell: a bar whose width is how much of the market
 * is off school, and a dot when somebody has a public holiday.
 */
export function HolidayHeaderHint({ day, de }: { day: HolidayDay | undefined; de: boolean }) {
  if (!day || (day.density === 0 && day.publicDays.length === 0)) {
    return <div className="h-1" />
  }
  return (
    <div className="h-1 flex items-center justify-center gap-0.5 px-1"
      title={holidayTitle(day, de)}>
      {day.density > 0 && (
        <span
          className="h-1 rounded-full bg-warn/70"
          style={{ width: `${Math.round(25 + day.density * 75)}%` }}
        />
      )}
      {day.publicDays.length > 0 && (
        <span className="w-1 h-1 rounded-full bg-danger shrink-0" />
      )}
    </div>
  )
}

/**
 * The footer line: the ODbL credit, plus a warning for any region whose ministry has
 * not published this far ahead. Without it an empty region reads as "no holidays"
 * rather than "not known yet".
 */
export function HolidayFootnote({
  coverage, start, regionCount,
}: { coverage: HolidayCoverage[]; start: string; regionCount: number }) {
  const { lang } = useI18n()
  const de = lang === 'de'
  if (regionCount === 0) return null

  const unpublished = coverage.filter((c) => coverageFor(c, start) !== 'covered')

  return (
    <span className="hidden lg:flex items-center gap-2 text-[10px] text-muted">
      <span className="inline-flex items-center gap-1">
        <span className="w-3 h-1 rounded-full bg-warn/70" />
        {de ? 'Ferien' : 'School holidays'}
      </span>
      <span className="inline-flex items-center gap-1">
        <span className="w-1 h-1 rounded-full bg-danger" />
        {de ? 'Feiertag' : 'Public holiday'}
      </span>
      {unpublished.length > 0 && (
        <span className="text-warn">
          {de ? 'Noch nicht veröffentlicht: ' : 'Not yet published: '}
          {unpublished.map((c) => c.region_code).join(', ')}
        </span>
      )}
      {/* ODbL requires the credit wherever the data is shown. */}
      <span className="opacity-70">{HOLIDAY_ATTRIBUTION}</span>
    </span>
  )
}
